import React, {useCallback, useEffect, useState} from 'react'
import {useTranslation} from '@modifi/plugin-i18n'
import {Col, DangerAlert, H5, Row, SuccessAlert, ButtonLink, PreviousIcon} from '@modifi/ui'
import {CustomerUploadManager} from '@modifi/plugin-documents'
import {Form} from '@modifi/plugin-forms'

import {ROUTE_COMPANY_PROFILE_BENEFICIAL_OWNERS} from '../../../../routes/constants'
import {BeneficialOwnerFormData} from '../constants'
import PersonFormFooter from '../../components/person-form-footer'
import {FormContentWrapper} from '../../../../ui/form'
import {EntityFormData, SelectOption} from '../../../../types/common'

import BeneficialOwnersFormContent from '../components/beneficial-owner-form-content'

export type CreateBeneficialOwnerProps = {
  isLoading: boolean
  updatePending: boolean
  initialize: () => void
  formData: EntityFormData<BeneficialOwnerFormData>
  onSubmit: (changes: Record<string, unknown>) => void
  onDiscard: () => void
  hasError: boolean
  errorMessage?: string
  dataSources: SelectOption[]
  onSelectDataSource: (value: string) => void
  pageIsVerified: boolean
  redirect: (route: string) => void
}

const CreateBeneficialOwner: React.FC<CreateBeneficialOwnerProps> = ({
  updatePending,
  hasError,
  errorMessage,
  isLoading,
  onSubmit,
  initialize,
  formData,
  onDiscard,
  dataSources,
  onSelectDataSource,
  pageIsVerified,
  redirect,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  const [currentPage, setCurrentPage] = useState(1)
  const [submitLabel, setSubmitLabel] = useState(t('sub-section-footer.cta-next') as string)

  useEffect(() => {
    initialize()
  }, [initialize])

  useEffect(() => {
    onDiscard()
  }, [onDiscard])

  // setting submit button label when page changes
  useEffect(() => {
    if (currentPage === 1) {
      setSubmitLabel(t('sub-section-footer.cta-next'))
    } else if (currentPage === 2) {
      if (dataSources?.length > 0) {
        setSubmitLabel(t('sub-section-footer.cta-next'))
      } else {
        setSubmitLabel(t('sub-section-footer.cta-submit'))
      }
    } else if (currentPage === 3) {
      setSubmitLabel(t('sub-section-footer.cta-submit'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  const handleSubmit = useCallback(
    (changes: Record<string, unknown>) => {
      if (currentPage === 1 || (currentPage === 2 && dataSources.length > 0)) {
        setCurrentPage(currentPage + 1)
      } else {
        onSubmit(changes)
      }
    },
    [onSubmit, currentPage, dataSources.length]
  )

  const handleBackButtonClick = useCallback(() => {
    setCurrentPage(currentPage - 1)
  }, [currentPage])

  if (isLoading) {
    return null
  }
  return (
    <CustomerUploadManager>
      {pageIsVerified && (
        <SuccessAlert>{t('ultimate-beneficial-owner-page.fully-verified-alert')}</SuccessAlert>
      )}
      {hasError && errorMessage && <DangerAlert>{errorMessage}</DangerAlert>}
      <Row>
        <Col>
          <ButtonLink
            data-target="ubo-summary-page"
            onClick={() => redirect(ROUTE_COMPANY_PROFILE_BENEFICIAL_OWNERS)}
          >
            <PreviousIcon /> {t('summary-page.back-button.label')}
          </ButtonLink>
        </Col>
      </Row>

      <Row>
        <Col mb={40}>
          <H5>{t('ultimate-beneficial-owner-create-page.heading')}</H5>
        </Col>
      </Row>

      <Form
        isLoading={updatePending}
        // @ts-expect-error the forms plugin types these as the DOM FormData
        defaultValues={formData.defaultValues}
        // @ts-expect-error the forms plugin types these as the DOM FormData
        initialValues={formData.initialValues}
        onSubmit={handleSubmit}
        onDiscard={onDiscard}
      >
        <FormContentWrapper>
          <BeneficialOwnersFormContent
            t={t}
            isNewBeneficialOwner
            selectPersonAsDataSource={onSelectDataSource}
            dataSourceOptions={dataSources}
            currentPage={currentPage}
            hasAnyRoleFieldItem
          />
        </FormContentWrapper>

        <PersonFormFooter
          loading={updatePending}
          hasError={hasError}
          submitLabel={submitLabel}
          canMoveBack={currentPage !== 1}
          handleBackButtonClick={handleBackButtonClick}
          isFinalStep={dataSources.length > 0 ? currentPage === 3 : currentPage === 2}
        />
      </Form>
    </CustomerUploadManager>
  )
}

export default CreateBeneficialOwner
